import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import moment from "moment-timezone";
import { toast } from "sonner";
import { BriefcaseBusiness, CalendarDays, Clock, Coffee, MapPin, User } from "lucide-react";
import { Textarea } from "../../../../../components/ui/textarea";
import Button from "../../../../../components/shared/Button";
import Sheet from "../../../../../components/shared/Sheet";
import BaseForm, { Field } from "../../../../../components/shared/Form";
import Avatar from "../../../../../components/shared/Avatar";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import useApi from "../../../../../utils/api";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { renderSectionsDuration } from "../../helpers/eventdisplay/renderSectionsDuration";
import { calculateSections } from "../../hooks/form/useCalculatedSections";
import { renderDuration } from "../../helpers/eventdisplay/renderDuration";
import { getErrorMessage } from "../../../../../utils/helpers/errors";
import { getPrimaryActiveResource, renderUserName } from "../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { withLoadingToast } from "../../helpers/withLoadingToast";
import type { Section } from "../../types/Event";
import type { Resource } from "../../types/Resource";

export type ClaimShiftActionType = "claim" | "deny";

export interface ClaimShiftValues {
    id: string;
    actionType: ClaimShiftActionType;
    status?: string;
    startDate?: string | Date;
    sections?: Section[];
    user?: Resource;
    requiredToBeApprovedBy?: string[];
    close?: () => void;
}

interface ClaimShiftProps {
    values: ClaimShiftValues | null;
    visible: boolean;
    close: () => void;
    queryOptions: { queryKey: QueryKey };
}

interface CalculatedSection extends Omit<Section, "start" | "end" | "duration"> {
    start: moment.Moment;
    end: moment.Moment;
    duration: string;
    type?: string;
}

type ClaimShiftFormValues = {
    reason: string;
};

function ClaimShift({ values, visible, close, queryOptions }: ClaimShiftProps) {
    const { t } = useTranslation();
    const userTimezone = moment.tz.guess();
    const companyTimeFormat = useCompanyTimeFormat();
    const queryClient = useQueryClient();
    const { dispatch } = useScheduleContext();
    const { authPut } = useApi();

    const form = useForm<ClaimShiftFormValues>({
        defaultValues: { reason: "" }
    });

    const approveMutate = useMutation({
        ...withLoadingToast(t("approving")),
        mutationFn: async ({ ids, approveReason }: { ids: string[]; approveReason: string }) => {
            return Promise.all(
                ids.map((id) => authPut(`/request/${id}/approve`, approveReason && { data: { approveReason } })),
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            toast.success(t("request-approved-successfully"));
            dispatch({ type: "refetch-events" });
            close();
            form.reset();
        },
        onError: (error) => {
            getErrorMessage(error, t);
            close();
        },
    });

    const denyMutate = useMutation({
        ...withLoadingToast(t("denying")),
        mutationFn: async ({ ids, reason }: { ids: string[]; reason: string }) => {
            return Promise.all(ids.map((id) => authPut(`/request/${id}/deny`, { data: { reason } })));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            toast.success(t("request-denied-successfully"));
            dispatch({ type: "refetch-events" })
            form.reset();
            close();
        },
        onError: (error) => {
            getErrorMessage(error, t);
            close();
        },
    });

    const isPending = approveMutate.isPending || denyMutate.isPending;

    const onFinish = useCallback((value: ClaimShiftFormValues) => {
        const ids = [values?.id].filter((id): id is string => !!id);
        if (values?.actionType === "claim") {
            approveMutate.mutate({ ids, approveReason: value?.reason });
        } else {
            denyMutate.mutate({ ids, reason: value?.reason });
        }
    }, [values, approveMutate, denyMutate]);

    const title = useMemo(() => {
        switch (values?.actionType) {
            case "claim":
                return t("claim-shift");
            default:
                return t("deny-shift");
        }
    }, [values?.actionType, t]);

    const primaryJob = useMemo(() => {
        return getPrimaryActiveResource(values?.user?.jobs || []);
    }, [values?.user?.jobs]);

    const primaryLocation = useMemo(() => {
        return getPrimaryActiveResource(values?.user?.locations || []);
    }, [values?.user?.locations]);

    const sections = useMemo((): CalculatedSection[] => {
        return calculateSections(values?.sections || [], values?.startDate) ?? [];
    }, [values]);

    const { totalBreaksDuration, breaks } = useMemo(() => {
        const totalBreaksDuration = sections?.reduce((total: number, section) => {
            if (section.type === "break" && section.start && section.end) {
                const start = moment(section.start);
                const end = moment(section.end);

                if (start.isValid() && end.isValid()) {
                    const diff = end.diff(start, "seconds");
                    return total + (Number(diff) || 0);
                }
            }
            return total;
        }, 0);

        return {
            totalBreaksDuration,
            breaks: sections?.filter((section) => section?.type === "break")
        }
    }, [sections]);

    return (
        <Sheet title={title} open={visible} close={close}>
            <div className="tw-border tw-p-3 tw-rounded-lg">
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
                    <CalendarDays size={19} className="tw-text-primary" />
                    <span className="tw-text-sm">
                        {moment.parseZone(sections?.[0]?.start).tz(userTimezone).format("ddd, DD MMMM YYYY")}
                    </span>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <Clock size={19} className="tw-text-primary" />
                        <span className="tw-text-sm">
                            {moment.parseZone(sections?.[0]?.start).tz(userTimezone).format(companyTimeFormat)}
                            {" - "}
                            {moment.parseZone(sections?.[sections?.length - 1]?.end).tz(userTimezone).format(companyTimeFormat)}
                        </span>
                    </div>
                    <div className="tw-bg-neutral-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-neutral-700 tw-font-semibold">
                        {renderSectionsDuration(
                            moment(sections?.[sections?.length - 1]?.end).diff(moment(sections?.[0]?.start), "seconds"),
                            sections
                        )}
                    </div>
                </div>

                <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <Coffee size={19} className="tw-text-primary" />
                        <span className="tw-text-sm tw-flex tw-items-center tw-gap-1">
                            {t("breaks")}
                            <p className="tw-text-neutral-500">{`(${breaks?.length})`}</p>
                        </span>
                    </div>
                    <div className="tw-bg-amber-100 tw-text-sm tw-px-3 tw-py-1 tw-rounded tw-text-amber-900 tw-font-semibold">
                        {totalBreaksDuration > 0 ? renderDuration(totalBreaksDuration) : "0h"}
                    </div>
                </div>

                {(sections?.[0]?.job || primaryJob) && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <BriefcaseBusiness size={19} className="tw-text-primary" />
                            <span className="tw-text-sm">{t("job")}</span>
                        </div>
                        <div className="tw-bg-primary/20 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-primary tw-font-semibold">
                            {renderSettingsResourceLabel(sections?.[0]?.job || primaryJob)}
                        </div>
                    </div>
                )}

                {(sections?.[0]?.location || primaryLocation) && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <MapPin size={19} className="tw-text-primary" />
                            <span className="tw-text-sm">{t("location")}</span>
                        </div>
                        <div className="tw-bg-purple-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-purple-950 tw-font-semibold">
                            {renderSettingsResourceLabel(sections?.[0]?.location || primaryLocation)}
                        </div>
                    </div>
                )}

                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <User size={19} className="tw-text-primary" />
                        <span className="tw-text-sm">{t("user")}</span>
                    </div>
                    <div className="tw-flex  tw-gap-2 tw-items-center tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-font-semibold">
                        <Avatar
                            user={values?.user}
                            size="xs"
                            textStyles="text-xs"
                        />
                        {renderUserName(values?.user)}
                    </div>
                </div>
            </div>

            <BaseForm form={form} onFinish={onFinish} className="tw-py-3">
                <Field name="reason">
                    <Textarea
                        rows={3}
                        className="tw-resize-none field-sizing-fixed"
                        placeholder={t("reason")}
                    />
                </Field>

                <div className="tw-w-full tw-flex tw-gap-2 tw-items-center">
                    {values?.actionType === "deny" && (
                        <Button
                            variant="secondary"
                            className="tw-w-full"
                            disabled={isPending}
                            loading={denyMutate.isPending}
                        >
                            {t("deny")}
                        </Button>
                    )}

                    {values?.actionType === "claim" && (
                        <Button
                            className="tw-w-full"
                            disabled={isPending}
                            loading={approveMutate.isPending}
                        >
                            {t("claim-shift")}
                        </Button>
                    )}
                </div>
            </BaseForm>
        </Sheet>
    );
}

export default ClaimShift;
